import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import RateCalendar from './RateCalendar';
import './DataTable.css';

/**
 * DataTable Component
 *
 * Renders a generic query-result table emitted by the v2 agent core's
 * `data_table` SSE artifact. The backend sends the FULL result set here
 * (capped at 500 rows) while the model only receives a compact summary —
 * this is what replaces row dumps narrated into the answer text.
 *
 * Artifact shape: { title, toolName, columns: [{key, label, type}], rows, totalRowCount }
 * Column types: string | number | date | currency | percent
 */

const DISPLAY_ROW_CAP = 100;

// Linked cells arrive as {text, href} (e.g. competitor rate shops link each
// rate to its OTA/brand-site booking page). Everything downstream — heatmap,
// sorting, formatting — operates on the text.
function cellText(value) {
  return value && typeof value === 'object' && !Array.isArray(value) && 'text' in value
    ? value.text
    : value;
}

function formatCell(value, type) {
  const href = value && typeof value === 'object' && value.href ? value.href : null;
  value = cellText(value);
  if (href) {
    return (
      <a className="ai-data-table-link" href={href} target="_blank" rel="noopener noreferrer">
        {formatCell(value, type)}
      </a>
    );
  }
  if (value === null || value === undefined || value === '') return '—';
  switch (type) {
    case 'currency': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      const prefix = num < 0 ? '-$' : '$';
      return `${prefix}${Math.abs(Math.round(num)).toLocaleString()}`;
    }
    case 'percent': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      return `${num.toFixed(1)}%`;
    }
    case 'number': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      return num.toLocaleString();
    }
    case 'date':
      return String(value).split('T')[0];
    default:
      return String(value);
  }
}

// Rate-shop grids get red→green conditional formatting scaled to the single
// lowest/highest displayed nightly rate across the WHOLE grid (all properties
// and dates): red = lowest, green = highest. Cells parse their leading $
// figure ("$190 · $215 total" → 190); non-rate cells ("Not shown") stay
// uncolored.
const HEATMAP_TOOLS = new Set(['get_competitor_rates']);

function parseHeatValue(value) {
  value = cellText(value);
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const match = String(value).match(/\$\s?([\d,]+(?:\.\d+)?)/);
  return match ? parseFloat(match[1].replace(/,/g, '')) : null;
}

function heatStyle(value, domain) {
  if (!domain) return undefined;
  const num = parseHeatValue(value);
  if (num === null) return undefined;
  const t = (num - domain.min) / (domain.max - domain.min);
  const hue = 4 + t * 138; // red (low) → green (high)
  return { backgroundColor: `hsla(${hue}, 70%, 45%, ${0.14 + t * 0.08})` };
}

function compareValues(a, b, type) {
  a = cellText(a);
  b = cellText(b);
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (type === 'number' || type === 'currency' || type === 'percent') {
    return Number(a) - Number(b);
  }
  return String(a).localeCompare(String(b));
}

const DataTable = ({ table }) => {
  const [sort, setSort] = useState(null); // { key, dir: 1|-1 }
  // Multi-date rate shops carry a structured hotel × night matrix alongside
  // the flat rows — render the calendar instead of the 35-row repeat-grid.
  const rateCalendar = table?.rateCalendar;

  const columns = Array.isArray(table?.columns) ? table.columns : [];
  const rows = useMemo(() => {
    const base = Array.isArray(table?.rows) ? table.rows : [];
    if (!sort) return base;
    const col = columns.find(c => c.key === sort.key);
    return [...base].sort((a, b) => sort.dir * compareValues(a[sort.key], b[sort.key], col?.type));
  }, [table, sort, columns]);

  const heatDomain = useMemo(() => {
    if (!(table?.heatmap === true || HEATMAP_TOOLS.has(table?.toolName))) return null;
    let min = Infinity;
    let max = -Infinity;
    for (const row of (Array.isArray(table?.rows) ? table.rows : [])) {
      for (const col of columns) {
        const num = parseHeatValue(row[col.key]);
        if (num === null) continue;
        if (num < min) min = num;
        if (num > max) max = num;
      }
    }
    return Number.isFinite(min) && Number.isFinite(max) && max > min ? { min, max } : null;
  }, [table, columns]);

  // After the hooks (rules-of-hooks): delegate to the calendar matrix when
  // the artifact carries one.
  if (rateCalendar && Array.isArray(rateCalendar.hotels) && rateCalendar.hotels.length > 0) {
    return <RateCalendar calendar={rateCalendar} title={table.title} />;
  }

  if (columns.length === 0 || rows.length === 0) return null;

  const displayRows = rows.slice(0, DISPLAY_ROW_CAP);
  const totalRowCount = table.totalRowCount || rows.length;

  const toggleSort = (key) => {
    setSort(prev => (prev?.key === key ? (prev.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }));
  };

  return (
    <div className="ai-data-table">
      {table.title && <div className="ai-data-table-title">{table.title}</div>}
      <div className="ai-data-table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`ai-data-table-th ${col.type === 'string' || col.type === 'date' ? 'left' : 'right'}`}
                  onClick={() => toggleSort(col.key)}
                  title="Sort"
                >
                  <span>{col.label}</span>
                  {sort?.key === col.key && (sort.dir === 1
                    ? <ChevronUp size={12} className="ai-data-table-sort-icon" />
                    : <ChevronDown size={12} className="ai-data-table-sort-icon" />)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => (
              <tr key={i}>
                {columns.map(col => (
                  <td
                    key={col.key}
                    className={col.type === 'string' || col.type === 'date' ? 'left' : 'right'}
                    style={heatStyle(row[col.key], heatDomain)}
                  >
                    {formatCell(row[col.key], col.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(totalRowCount > displayRows.length) && (
        <div className="ai-data-table-footer">Showing {displayRows.length} of {totalRowCount} rows</div>
      )}
    </div>
  );
};

export default DataTable;
